import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, message } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import { getSoilSolutionAnalysisInfo, updateSoilSolutionAnalysis } from '../../../Utils/analysis/soilSolutionAnalysis';
import CustomDatePicker from '../../Shared/CustomDatePicker';

const EditSoilSolution = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { exploitationId, analysisId } = useParams();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAnalysisDetails();
    }, [analysisId]);

    const fetchAnalysisDetails = async () => {
        try {
            const details = await getSoilSolutionAnalysisInfo(analysisId);
            form.setFieldsValue({
                ...details.data,
                date_realisation: moment(details.date_realisation),
            });
        } catch (error) {
            console.error('Erreur lors de la récupération des détails de l\'analyse:', error);
            message.error('Échec de la récupération des détails de l\'analyse');
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                date_realisation: values.date_realisation.toISOString(),
                exploitation_id: parseInt(exploitationId),
            };
            await updateSoilSolutionAnalysis(analysisId, formattedValues);
            message.success('Analyse de solution du sol mise à jour avec succès');
            navigate(`/admin/exploitation/${exploitationId}/soil-solution`);
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'analyse de solution du sol:', error);
            message.error('Échec de la mise à jour de l\'analyse de solution du sol');
        } finally {
            setLoading(false);
        }
    };

    const soilSolutionParameters = [
        { key: 'calcium', label: 'Calcium', unit: 'mg/L' },
        { key: 'magnesium', label: 'Magnésium', unit: 'mg/L' },
        { key: 'potassium', label: 'Potassium', unit: 'mg/L' },
        { key: 'sodium', label: 'Sodium', unit: 'mg/L' },
        { key: 'ammonium', label: 'Ammonium', unit: 'mg/L' },
        { key: 'bicarbonates', label: 'Bicarbonates', unit: 'mg/L' },
        { key: 'nitrates', label: 'Nitrates', unit: 'mg/L' },
        { key: 'chlorures', label: 'Chlorures', unit: 'mg/L' },
        { key: 'phosphates', label: 'Phosphates', unit: 'mg/L' },
        { key: 'sulfates', label: 'Sulfates', unit: 'mg/L' },
        { key: 'fer', label: 'Fer', unit: 'mg/L' },
        { key: 'cuivre', label: 'Cuivre', unit: 'mg/L' },
        { key: 'manganese', label: 'Manganèse', unit: 'mg/L' },
        { key: 'zinc', label: 'Zinc', unit: 'mg/L' },
        { key: 'molybdene', label: 'Molybdène', unit: 'mg/L' },
        { key: 'bore', label: 'Bore', unit: 'mg/L' },
    ];

    const otherParameters = [
        { key: 'somme_anion', label: 'Somme des anions', unit: 'meq/L' },
        { key: 'somme_cation', label: 'Somme des cations', unit: 'meq/L' },
        { key: 'ph', label: 'pH', unit: '' },
        { key: 'ec', label: 'Conductivité électrique (EC)', unit: 'mS/cm' },
    ];

    const HeaderTab = ({ text }) => (
        <th className="border border-gray-300 bg-gray-300 px-2 py-2">{text}</th>
    );

    return (
        <div className="bg-white h-full">
            <Header title="Modifier l'Analyse de Solution du Sol" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`/admin/exploitation/${exploitationId}/analysis/solution-sol`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />{" "}
                    <div className="font-semibold text-base text-indigo-600">
                        {" "}
                        Retour
                    </div>
                </Link>
                <Form
                    form={form}
                    name="editSoilSolution"
                    onFinish={onFinish}
                    layout="vertical"
                    className='bg-white p-6 rounded-lg max-w-[1000px] shadow-md'
                >
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <Form.Item name="date_realisation" label="Date de réalisation" rules={[{ required: true }]}>
                            <CustomDatePicker />
                        </Form.Item>
                        <Form.Item name="laboratoire" label="Laboratoire">
                            <Input />
                        </Form.Item>
                        <Form.Item name="echantillon" label="Échantillon">
                            <Input />
                        </Form.Item>
                        <Form.Item name="vanne_associee" label="Vanne associée">
                            <Input />
                        </Form.Item>
                        
                        <Form.Item name="type_sol" label="Type de sol">
                            <Input />
                        </Form.Item>
                    </div>

                    <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres de la solution du sol</h2>
                    <table className="w-full border-collapse border border-gray-300 mb-4">
                        <thead>
                            <tr>
                                <HeaderTab text="Paramètre" />
                                <HeaderTab text="Valeur" />
                                <HeaderTab text="Unité" />
                            </tr>
                        </thead>
                        <tbody>
                            {soilSolutionParameters.map(param => (
                                <tr key={param.key}>
                                    <td className="border border-gray-300 px-2 py-2">{param.label}</td>
                                    <td className="border border-gray-300 px-2 py-2">
                                        <Form.Item name={param.key} noStyle>
                                            <InputNumber step={0.01} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </td>
                                    <td className="border border-gray-300 px-2 py-2">{param.unit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <h2 className="text-xl font-semibold mt-6 mb-4">Autres paramètres</h2>
                    <table className="w-full border-collapse border border-gray-300 mb-4">
                        <thead>
                            <tr>
                                <HeaderTab text="Paramètre" />
                                <HeaderTab text="Valeur" />
                                <HeaderTab text="Unité" />
                            </tr>
                        </thead>
                        <tbody>
                            {otherParameters.map(param => (
                                <tr key={param.key}>
                                    <td className="border border-gray-300 px-2 py-2">{param.label}</td>
                                    <td className="border border-gray-300 px-2 py-2">
                                        <Form.Item name={param.key} noStyle>
                                            <InputNumber step={0.01} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </td>
                                    <td className="border border-gray-300 px-2 py-2">{param.unit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <Form.Item className="mt-4">
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Mettre à jour l'Analyse de Solution du Sol
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export default EditSoilSolution;